import React from 'react';

// Small SVG bar chart. data: [{ label, value, value2? }]
export default function BarChart({ data, height = 180, primary = '#9C3D54', secondary = '#4D7C5F', horizontal = false, showValues = false, maxOverride }) {
  if (horizontal) return <HorizontalBar data={data} height={height} color={secondary} maxOverride={maxOverride} />;

  const max = maxOverride || Math.max(...data.map(d => Math.max(d.value || 0, d.value2 || 0)), 1);
  const yTicks = 4;
  const paddingLeft = 40;
  const paddingRight = 12;
  const paddingBottom = 28;
  const paddingTop = 12;
  const width = 900; // will be responsive via viewBox
  const innerWidth = width - paddingLeft - paddingRight;
  const innerHeight = height - paddingTop - paddingBottom;
  const step = innerWidth / data.length;
  const barW = data[0]?.value2 !== undefined ? Math.min(18, step * 0.28) : Math.min(56, step * 0.5);

  const yScale = (v) => innerHeight - (v / max) * innerHeight;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" preserveAspectRatio="none">
      {/* Y grid */}
      {Array.from({ length: yTicks + 1 }).map((_, i) => {
        const yv = (max / yTicks) * (yTicks - i);
        const y = paddingTop + (innerHeight / yTicks) * i;
        return (
          <g key={i}>
            <line x1={paddingLeft} x2={width - paddingRight} y1={y} y2={y} stroke="#E7E2D3" strokeWidth={1} />
            <text x={paddingLeft - 8} y={y + 4} fontSize={11} fill="#7a6f5c" textAnchor="end">{Math.round(yv)}</text>
          </g>
        );
      })}
      {/* Bars */}
      {data.map((d, i) => {
        const cx = paddingLeft + step * i + step / 2;
        if (d.value2 !== undefined) {
          const x1 = cx - barW - 3;
          const x2 = cx + 3;
          const h1 = innerHeight - yScale(d.value);
          const h2 = innerHeight - yScale(d.value2);
          return (
            <g key={i}>
              <rect x={x1} y={paddingTop + yScale(d.value)} width={barW} height={h1} fill={primary} />
              <rect x={x2} y={paddingTop + yScale(d.value2)} width={barW} height={h2} fill={secondary} />
              <text x={cx} y={height - 8} fontSize={11} fill="#7a6f5c" textAnchor="middle">{d.label}</text>
            </g>
          );
        }
        const h = innerHeight - yScale(d.value);
        return (
          <g key={i}>
            <rect x={cx - barW / 2} y={paddingTop + yScale(d.value)} width={barW} height={h} fill={primary} />
            <text x={cx} y={height - 8} fontSize={11} fill="#7a6f5c" textAnchor="middle">{d.label}</text>
            {showValues && <text x={cx} y={paddingTop + yScale(d.value) - 4} fontSize={11} fill="#7a6f5c" textAnchor="middle">{d.value}</text>}
          </g>
        );
      })}
    </svg>
  );
}

function HorizontalBar({ data, height = 180, color = '#4D7C5F', maxOverride }) {
  const max = maxOverride || Math.max(...data.map(d => d.value), 1);
  const paddingLeft = 90;
  const paddingRight = 20;
  const paddingBottom = 24;
  const paddingTop = 4;
  const width = 900;
  const innerWidth = width - paddingLeft - paddingRight;
  const innerHeight = height - paddingTop - paddingBottom;
  const step = innerHeight / data.length;
  const barH = Math.min(20, step * 0.6);
  const xTicks = 4;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full">
      {Array.from({ length: xTicks + 1 }).map((_, i) => {
        const xv = (max / xTicks) * i;
        const x = paddingLeft + (innerWidth / xTicks) * i;
        return (
          <g key={i}>
            <line x1={x} x2={x} y1={paddingTop} y2={height - paddingBottom} stroke="#E7E2D3" strokeWidth={1} />
            <text x={x} y={height - 8} fontSize={11} fill="#7a6f5c" textAnchor="middle">{Math.round(xv)}</text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const y = paddingTop + step * i + (step - barH) / 2;
        const w = (d.value / max) * innerWidth;
        return (
          <g key={i}>
            <text x={paddingLeft - 8} y={y + barH * 0.72} fontSize={11} fill="#7a6f5c" textAnchor="end">{d.label}</text>
            <rect x={paddingLeft} y={y} width={w} height={barH} fill={color} />
          </g>
        );
      })}
    </svg>
  );
}
